import { useState } from 'react';
import { X, Plus, BarChart3, Sparkles, Lock } from 'lucide-react';
import { futuresSpecs, safeTicks } from '../../config/futuresSpecs';
import { autoTagTradesFromEvents, diffTagApplications, DEFAULT_PROXIMITY_MIN } from '../../utils/eventAutoTag';
import { applyTradingDayBoundary, friendlyToIana, convertToEt, formatEtDate, getTradingDay, SESSION_TZ } from '../../services/timeService';
import { parseCsvToBars } from '../../services/barData/csvBarParser';
import GlassSelect from '../Common/GlassSelect';

// Convert (calendarDate, hhmm) wall-clock pair from sourceTz to ET, in place.
// Returns { date, time }. If sourceTz is already ET, returns inputs as-is.
// Used by every CSV parser to enforce the trade.entryTime=ET storage contract
// regardless of the user's NinjaTrader/Tradovate timezone configuration.
const normalizeToEt = (calendarDate, hhmm, sourceTz) => {
  if (!calendarDate || !hhmm || !sourceTz || sourceTz === SESSION_TZ) {
    return { date: calendarDate, time: hhmm };
  }
  const conv = convertToEt(calendarDate, hhmm, sourceTz);
  return conv || { date: calendarDate, time: hhmm };
};

/**
 * Auto-tag imported trades against any economic events already saved for
 * their respective dates. Same logic JournalView's per-day effect uses,
 * but applied as a ONE-SHOT pass at import time so a CSV spanning many
 * dates doesn't have to wait for the user to visit each day to trigger
 * tagging.
 *
 * For each unique date in the import:
 *   - Read journalEntries[date]?.newsEvents
 *   - If non-empty, run autoTagTradesFromEvents on the trades for that date
 *
 * Returns: { tagged: Trade[], stats: { totalTagged, eventsApplied } }
 */
const autoTagImportedTrades = (newTrades, journalEntries) => {
  if (!Array.isArray(newTrades) || newTrades.length === 0) {
    return { tagged: newTrades || [], stats: { totalTagged: 0, eventsApplied: 0 } };
  }

  // Group by trade.date so we can fetch the right journalEntry for each batch
  const byDate = new Map();
  for (const t of newTrades) {
    const d = t?.date;
    if (!d) continue;
    if (!byDate.has(d)) byDate.set(d, []);
    byDate.get(d).push(t);
  }

  if (byDate.size === 0) {
    // No trades had a date — pass them through untagged
    return { tagged: newTrades, stats: { totalTagged: 0, eventsApplied: 0 } };
  }

  // Build a Map<id, taggedTrade> for the trades we touched, then merge
  // back into the original order so anything without a date still flows.
  const taggedById = new Map();
  let totalTagged = 0;
  let eventsApplied = 0;

  for (const [date, dateTrades] of byDate.entries()) {
    const events = Array.isArray(journalEntries?.[date]?.newsEvents)
      ? journalEntries[date].newsEvents
      : [];
    if (events.length === 0) continue;

    const tagged = autoTagTradesFromEvents(dateTrades, events, { windowMin: DEFAULT_PROXIMITY_MIN });
    const { changedCount } = diffTagApplications(dateTrades, tagged);
    if (changedCount === 0) continue;

    totalTagged += changedCount;
    eventsApplied += events.length;
    for (const t of tagged) {
      if (t?.id != null) taggedById.set(t.id, t);
    }
  }

  if (taggedById.size === 0) {
    return { tagged: newTrades, stats: { totalTagged: 0, eventsApplied: 0 } };
  }

  // Merge back: trades we tagged get the tagged version, others pass through
  const finalTrades = newTrades.map(t =>
    (t?.id != null && taggedById.has(t.id)) ? taggedById.get(t.id) : t
  );

  return { tagged: finalTrades, stats: { totalTagged, eventsApplied } };
};

/**
 * Parse ATM template name to extract stop loss, contracts, etc.
 * Example: "1) 21 - 12pSL - 5C - 2-3R - 4AT - 221   ($120 Risk - 1 Runner)"
 */
const parseAtmTemplateName = (templateName) => {
  if (!templateName) return { name: '', stopLossPoints: null, contractCount: null, riskAmount: null };
  
  // Remove leading number prefix like "0) " or "1) 21 - " 
  let name = templateName.replace(/^\d+\)\s*(\d+\s*-\s*)?/, '').trim();
  
  // Extract stop loss points from pattern like "12pSL"
  const slMatch = name.match(/(\d+)pSL/i);
  const stopLossPoints = slMatch ? parseInt(slMatch[1]) : null;
  
  // Extract contract count from pattern like "5C"
  const contractMatch = name.match(/(\d+)C(?:\s|$|-)/i);
  const contractCount = contractMatch ? parseInt(contractMatch[1]) : null;
  
  // Extract risk amount from patterns like "($120 Risk"
  const riskMatch = name.match(/\$(\d+)\s*Risk/i);
  const riskAmount = riskMatch ? parseInt(riskMatch[1]) : null;
  
  // Clean up the name
  name = name.replace(/\s*\([^)]*\$[^)]*\)\s*$/, '').trim();
  
  return { name, stopLossPoints, contractCount, riskAmount };
};

// Helper function to convert NinjaTrader timestamp to HH:MM format
// "1/30/2026 3:17:39 PM" -> "15:17"
const convertTimestampToTimeInput = (timestamp) => {
  if (!timestamp) return '';
  const parts = timestamp.split(' ');
  if (parts.length < 3) return '';
  
  const timePart = parts[1];
  const period = parts[2]?.toUpperCase();
  
  if (!timePart || !period) return '';
  
  const [hourStr, minuteStr] = timePart.split(':');
  let hour = parseInt(hourStr, 10);
  const minute = minuteStr?.padStart(2, '0') || '00';
  
  if (period === 'PM' && hour !== 12) {
    hour += 12;
  } else if (period === 'AM' && hour === 12) {
    hour = 0;
  }
  
  return `${hour.toString().padStart(2, '0')}:${minute}`;
};

// Helper to format Tradovate timestamp to HH:MM
const formatTradovateTime = (timestamp) => {
  if (!timestamp) return '';
  try {
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) {
      const parts = timestamp.split(' ');
      if (parts.length >= 2) {
        const timePart = parts[1];
        const period = parts[2]?.toUpperCase();
        const [h, m] = timePart.split(':');
        let hour = parseInt(h);
        if (period === 'PM' && hour !== 12) hour += 12;
        else if (period === 'AM' && hour === 12) hour = 0;
        return `${hour.toString().padStart(2, '0')}:${m}`;
      }
      return '';
    }
    return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
  } catch {
    return '';
  }
};

// Helper to format Tradovate timestamp to YYYY-MM-DD
const formatTradovateDate = (timestamp) => {
  if (!timestamp) return '';
  try {
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) {
      const datePart = timestamp.split(' ')[0];
      if (datePart && datePart.includes('/')) {
        const [m, d, y] = datePart.split('/');
        return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
      }
      return '';
    }
    // ET-anchored YYYY-MM-DD — matches the trade.date storage contract.
    return formatEtDate(date);
  } catch {
    return '';
  }
};

// applyTradingDayBoundary moved to src/services/timeService.js — imported above.


// Parse CSV - supports NinjaTrader and generic formats
// userTimezone (IANA) tells us what TZ the CSV's wall-clock entry/exit times
// are in (typically the user's NinjaTrader-configured TZ). Times are normalized
// to ET on storage per the trade.entryTime=ET contract.
const parseCSV = (csvText, userTimezone = SESSION_TZ) => {
  const lines = csvText.trim().split('\n');
  if (lines.length < 2) return [];
  
  const headers = lines[0].toLowerCase().split(',').map(h => h.trim().replace(/"/g, ''));
  const parsedTrades = [];
  
  for (let i = 1; i < lines.length; i++) {
    const values = [];
    let current = '';
    let inQuotes = false;
    for (const char of lines[i]) {
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        values.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    values.push(current.trim());
    
    if (values.length < 3) continue;
    
    const trade = {
      date: '', symbol: '', setup: '', entry: '', exit: '',
      stopLoss: '', quantity: '1', tags: [], mistakes: [],
      notes: '', accountId: ''
    };
    
    headers.forEach((header, index) => {
      const value = values[index] || '';
      
      if (header === 'date' || header === 'trade date') {
        trade.date = value;
      } else if (header === 'exit time' || header === 'exittime') {
        trade.exitTimeRaw = value;
        trade.exitTime = convertTimestampToTimeInput(value);
        // Only set date from exit time if we don't have entry time date
        // Entry time is the correct basis for trading day, not exit time
        if (!trade.date) {
          const datePart = value.split(' ')[0];
          if (datePart) {
            const parts = datePart.split('/');
            if (parts.length === 3) {
              trade.date = `${parts[2]}-${parts[0].padStart(2, '0')}-${parts[1].padStart(2, '0')}`;
            } else {
              trade.date = datePart;
            }
          }
        }
      } else if (header === 'symbol' || header === 'instrument') {
        let sym = value.split(' ')[0].toUpperCase();
        sym = sym.replace(/[HMUZ]\d{1,2}$/, '');
        trade.symbol = sym;
      } else if (header === 'setup') {
        trade.setup = value;
      } else if (header === 'strategy') {
        trade.atm = value;
      } else if (header === 'entry' || header === 'entry price' || header === 'entryprice' || header === 'avg. entry') {
        trade.entry = value.replace(/[$,]/g, '');
      } else if (header === 'exit' || header === 'exit price' || header === 'exitprice' || header === 'avg. exit') {
        trade.exit = value.replace(/[$,]/g, '');
      } else if (header === 'stop' || header === 'stop loss' || header === 'stoploss') {
        trade.stopLoss = value.replace(/[$,]/g, '');
      } else if (header === 'quantity' || header === 'qty' || header === 'contracts') {
        trade.quantity = value.replace(/[^0-9]/g, '') || '1';
      } else if (header === 'notes' || header === 'note' || header === 'comment') {
        trade.notes = value;
      } else if (header === 'account' || header === 'accountid' || header === 'account id') {
        trade.accountId = value.split('!')[0].trim();
      } else if (header === 'market pos.' || header === 'marketpos' || header === 'position') {
        trade.direction = value.toLowerCase();
      } else if (header === 'entry time' || header === 'entrytime') {
        trade.entryTimeRaw = value;
        trade.entryTime = convertTimestampToTimeInput(value);
        // Extract calendar date from ENTRY time (this determines trading day, not exit time)
        const entryDatePart = value.split(' ')[0];
        if (entryDatePart) {
          const entryParts = entryDatePart.split('/');
          if (entryParts.length === 3) {
            trade.date = `${entryParts[2]}-${entryParts[0].padStart(2, '0')}-${entryParts[1].padStart(2, '0')}`;
          }
        }
      } else if (header === 'profit' || header === 'pnl' || header === 'p&l') {
        let profitStr = value.replace(/[$,]/g, '');
        if (profitStr.startsWith('(') && profitStr.endsWith(')')) {
          profitStr = '-' + profitStr.slice(1, -1);
        }
        trade.ntProfit = profitStr;
      } else if (header === 'mae') {
        let maeStr = value.replace(/[$,]/g, '');
        if (maeStr.startsWith('(') && maeStr.endsWith(')')) {
          maeStr = '-' + maeStr.slice(1, -1);
        }
        trade.mae = maeStr;
      } else if (header === 'mfe') {
        let mfeStr = value.replace(/[$,]/g, '');
        if (mfeStr.startsWith('(') && mfeStr.endsWith(')')) {
          mfeStr = '-' + mfeStr.slice(1, -1);
        }
        trade.mfe = mfeStr;
      } else if (header === 'atm' || header === 'atm strategy' || header === 'atmstrategy') {
        trade.atm = value;
      } else if (header === 'commission' || header === 'comm' || header === 'commissions') {
        let commStr = value.replace(/[$,]/g, '');
        if (commStr.startsWith('(') && commStr.endsWith(')')) {
          commStr = '-' + commStr.slice(1, -1);
        }
        trade.importedCommission = Math.abs(parseFloat(commStr)) || 0;
      }
    });
    
    // Normalize wall-clock entry/exit times to ET (the storage contract).
    // For ET users (default), this is a no-op. For non-ET configurations, it
    // converts the source-TZ wall clock to ET before the boundary check.
    if (trade.entryTime) {
      const conv = normalizeToEt(trade.date, trade.entryTime, userTimezone);
      trade.date = conv.date;
      trade.entryTime = conv.time;
    }
    if (trade.exitTime) {
      const exitConv = normalizeToEt(trade.date, trade.exitTime, userTimezone);
      trade.exitTime = exitConv.time;
    }

    // Apply trading day boundary (6pm ET rule) based on entry time
    if (trade.entryTime) {
      trade.date = applyTradingDayBoundary(trade.date, trade.entryTime);
    }

    if (trade.date && trade.symbol && trade.entry && trade.exit) {
      parsedTrades.push(trade);
    }
  }

  return parsedTrades;
};


// Parse Tradovate Fills.csv format - matches buy/sell fills into complete trades
const parseTradovateFills = (csvText, userTimezone = SESSION_TZ) => {
  const lines = csvText.trim().split('\n');
  if (lines.length < 2) return [];
  
  const headers = lines[0].toLowerCase().split(',').map(h => h.trim().replace(/"/g, ''));
  const fills = [];
  
  for (let i = 1; i < lines.length; i++) {
    const values = [];
    let current = '';
    let inQuotes = false;
    for (const char of lines[i]) {
      if (char === '"') inQuotes = !inQuotes;
      else if (char === ',' && !inQuotes) { values.push(current.trim()); current = ''; }
      else current += char;
    }
    values.push(current.trim());
    
    if (values.length < 5) continue;
    
    const fill = {};
    headers.forEach((header, index) => {
      const value = values[index] || '';
      
      if (header === '_id' || header === 'fill id') fill.fillId = value;
      else if (header === 'account') fill.accountId = value.trim();
      else if (header === 'contract' || header === 'product') {
        let sym = value.split(' ')[0].toUpperCase();
        sym = sym.replace(/[HMUZ]\d{1,2}$/, '');
        if (sym) fill.symbol = sym;
      }
      else if (header === 'b/s') fill.side = value.trim().toLowerCase();
      else if (header === '_action') fill.side = value === '0' ? 'buy' : 'sell';
      else if (header === 'quantity' || header === '_qty') fill.quantity = parseInt(value) || 1;
      else if (header === 'price' || header === '_price') fill.price = parseFloat(value.replace(/[$,]/g, '')) || 0;
      else if (header === 'timestamp' || header === '_timestamp') {
        fill.timestamp = value;
        if (value.includes('T') || value.includes('Z')) {
          fill.date = value.split('T')[0];
        } else {
          const datePart = value.split(' ')[0];
          if (datePart && datePart.includes('/')) {
            const parts = datePart.split('/');
            if (parts.length === 3) {
              fill.date = `${parts[2]}-${parts[0].padStart(2, '0')}-${parts[1].padStart(2, '0')}`;
            }
          }
        }
        if (value.includes(' ')) {
          const timePart = value.split(' ')[1];
          if (timePart) {
            const timeOnly = timePart.split('.')[0];
            const [h, m] = timeOnly.split(':');
            const period = value.split(' ')[2]?.toUpperCase();
            if (period === 'PM' && parseInt(h) !== 12) {
              fill.time = `${(parseInt(h) + 12).toString().padStart(2, '0')}:${m}`;
            } else if (period === 'AM' && parseInt(h) === 12) {
              fill.time = `00:${m}`;
            } else {
              fill.time = `${h.padStart(2, '0')}:${m}`;
            }
          }
        }
      }
      else if (header === 'commission') fill.commission = Math.abs(parseFloat(value.replace(/[$,]/g, ''))) || 0;
    });
    
    if (fill.fillId && fill.price && fill.side) fills.push(fill);
  }
  
  fills.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
  
  const trades = [];
  const openPositions = {};
  
  for (const fill of fills) {
    const key = `${fill.symbol}_${fill.accountId}`;
    if (!openPositions[key]) openPositions[key] = [];
    
    const positions = openPositions[key];
    const oppositeIndex = positions.findIndex(p => 
      (fill.side === 'buy' && p.side === 'sell') || (fill.side === 'sell' && p.side === 'buy')
    );
    
    if (oppositeIndex !== -1) {
      const entryFill = positions.splice(oppositeIndex, 1)[0];
      const exitFill = fill;
      const qty = Math.min(entryFill.quantity, exitFill.quantity);
      const isLong = entryFill.side === 'buy';
      
      // Normalize wall-clock fill times to ET, then apply 6pm boundary
      const calendarDate = entryFill.date || exitFill.date;
      const entryEt = normalizeToEt(calendarDate, entryFill.time, userTimezone);
      const exitEt  = normalizeToEt(calendarDate, exitFill.time, userTimezone);
      const tradingDate = applyTradingDayBoundary(entryEt.date, entryEt.time);

      trades.push({
        date: tradingDate,
        symbol: fill.symbol || 'MNQ',
        entry: entryFill.price.toString(),
        exit: exitFill.price.toString(),
        quantity: qty.toString(),
        direction: isLong ? 'long' : 'short',
        entryTime: entryEt.time || '',
        exitTime: exitEt.time || '',
        accountId: fill.accountId || '',
        importedCommission: (entryFill.commission || 0) + (exitFill.commission || 0),
        setup: '', stopLoss: '', tags: [], mistakes: [], notes: ''
      });
    } else {
      positions.push(fill);
    }
  }
  
  return trades;
};


// Parse Tradovate Performance.csv format - already has complete round-trip trades
const parseTradovatePerformance = (csvText, fillsData = null, userTimezone = SESSION_TZ) => {
  const lines = csvText.trim().split('\n');
  if (lines.length < 2) return [];
  
  const headers = lines[0].toLowerCase().split(',').map(h => h.trim().replace(/"/g, ''));
  const trades = [];
  
  // Build fills lookup map if available
  let fillsMap = {};
  if (fillsData) {
    const fillLines = fillsData.trim().split('\n');
    if (fillLines.length >= 2) {
      const fillHeaders = fillLines[0].toLowerCase().split(',').map(h => h.trim().replace(/"/g, ''));
      for (let i = 1; i < fillLines.length; i++) {
        const values = [];
        let current = '';
        let inQuotes = false;
        for (const char of fillLines[i]) {
          if (char === '"') inQuotes = !inQuotes;
          else if (char === ',' && !inQuotes) { values.push(current.trim()); current = ''; }
          else current += char;
        }
        values.push(current.trim());
        
        const fill = {};
        fillHeaders.forEach((h, idx) => {
          const v = values[idx] || '';
          if (h === '_id' || h === 'fill id') fill.fillId = v;
          else if (h === 'account') fill.accountId = v.trim();
          else if (h === 'commission') fill.commission = Math.abs(parseFloat(v.replace(/[$,]/g, ''))) || 0;
        });
        if (fill.fillId) fillsMap[fill.fillId] = fill;
      }
    }
  }
  
  for (let i = 1; i < lines.length; i++) {
    const values = [];
    let current = '';
    let inQuotes = false;
    for (const char of lines[i]) {
      if (char === '"') inQuotes = !inQuotes;
      else if (char === ',' && !inQuotes) { values.push(current.trim()); current = ''; }
      else current += char;
    }
    values.push(current.trim());
    
    if (values.length < 5) continue;
    
    const trade = {
      date: '', symbol: '', entry: '', exit: '', quantity: '1',
      direction: 'long', entryTime: '', exitTime: '', accountId: '',
      importedCommission: 0, setup: '', stopLoss: '', tags: [],
      mistakes: [], notes: '', ntProfit: ''
    };
    
    let buyFillId = '', sellFillId = '';
    let buyPrice = 0, sellPrice = 0;
    let boughtTimestamp = '', soldTimestamp = '';
    
    headers.forEach((header, index) => {
      const value = values[index] || '';
      
      if (header === 'symbol') {
        let sym = value.split(' ')[0].toUpperCase();
        sym = sym.replace(/[HMUZ]\d{1,2}$/, '');
        trade.symbol = sym || value;
      }
      else if (header === 'qty') trade.quantity = value || '1';
      else if (header === 'buyprice') buyPrice = parseFloat(value) || 0;
      else if (header === 'sellprice') sellPrice = parseFloat(value) || 0;
      else if (header === 'pnl') {
        let pnl = value.replace(/[$,]/g, '');
        if (pnl.startsWith('(') && pnl.endsWith(')')) pnl = '-' + pnl.slice(1, -1);
        trade.ntProfit = pnl;
      }
      else if (header === 'buyfillid') buyFillId = value;
      else if (header === 'sellfillid') sellFillId = value;
      else if (header === 'boughttimestamp') boughtTimestamp = value;
      else if (header === 'soldtimestamp') soldTimestamp = value;
      else if (header === 'duration') trade.duration = value;
    });
    
    const buyTime = new Date(boughtTimestamp);
    const sellTime = new Date(soldTimestamp);
    
    if (buyTime < sellTime) {
      trade.direction = 'long';
      trade.entry = buyPrice.toString();
      trade.exit = sellPrice.toString();
      trade.entryTime = formatTradovateTime(boughtTimestamp);
      trade.exitTime = formatTradovateTime(soldTimestamp);
      trade.date = formatTradovateDate(boughtTimestamp);
    } else {
      trade.direction = 'short';
      trade.entry = sellPrice.toString();
      trade.exit = buyPrice.toString();
      trade.entryTime = formatTradovateTime(soldTimestamp);
      trade.exitTime = formatTradovateTime(boughtTimestamp);
      trade.date = formatTradovateDate(soldTimestamp);
    }
    
    const buyFill = fillsMap[buyFillId];
    const sellFill = fillsMap[sellFillId];
    
    if (buyFill || sellFill) {
      trade.accountId = buyFill?.accountId || sellFill?.accountId || '';
      trade.importedCommission = (buyFill?.commission || 0) + (sellFill?.commission || 0);
    }
    
    // Normalize Tradovate entry/exit times to ET. formatTradovateTime returns
    // host-local hours (since `new Date(timestamp)` then `getHours()`), so for
    // non-ET hosts the stored time would violate the trade.entryTime=ET contract.
    if (trade.entryTime) {
      const conv = normalizeToEt(trade.date, trade.entryTime, userTimezone);
      trade.date = conv.date;
      trade.entryTime = conv.time;
    }
    if (trade.exitTime) {
      const exitConv = normalizeToEt(trade.date, trade.exitTime, userTimezone);
      trade.exitTime = exitConv.time;
    }

    // Apply trading day boundary (6pm ET rule) based on entry time
    if (trade.entryTime) {
      trade.date = applyTradingDayBoundary(trade.date, trade.entryTime);
    }

    if (trade.date && trade.symbol && trade.entry && trade.exit) {
      trades.push(trade);
    }
  }

  return trades;
};


// Friendly→IANA timezone mapping moved to src/services/timeService.js — use
// `friendlyToIana` (imported above). Local alias kept so call sites read
// naturally without churn.
const getIANATimezone = friendlyToIana;

// Parse MotiveWave execution fills CSV format
// Columns: Account, Ticket, Order ID, Underlying, Symbol, Exchange, Time, Action, Quantity, Price, Commission, Fill Id
// Time is UTC (e.g., "03/11/2026 13:54:09.086+0000"), Action is BOT/SLD
const parseMotiveWaveFills = (csvText, _userTimezone = 'America/New_York') => {
  const lines = csvText.trim().split('\n');
  if (lines.length < 2) return [];

  const headers = lines[0].toLowerCase().split(',').map(h => h.trim().replace(/"/g, ''));
  const fills = [];

  // Convert UTC timestamp to ET date and time
  const parseMotiveWaveTime = (raw) => {
    if (!raw) return { date: '', time: '', timestamp: '' };
    // "03/11/2026 13:54:09.086+0000" — UTC
    // Create a proper ISO string for Date parsing
    const clean = raw.replace(/"/g, '').trim();
    // Parse: MM/DD/YYYY HH:MM:SS.mmm+0000
    const match = clean.match(/(\d{2})\/(\d{2})\/(\d{4})\s+(\d{2}):(\d{2}):(\d{2})(?:\.\d+)?([+-]\d{4})?/);
    if (!match) return { date: '', time: '', timestamp: clean };

    const [, mo, dd, yyyy, hh, mm, ss, tz] = match;
    // Build ISO: YYYY-MM-DDTHH:MM:SS+0000
    const isoStr = `${yyyy}-${mo}-${dd}T${hh}:${mm}:${ss}${tz || '+0000'}`;
    const utcDate = new Date(isoStr);
    if (isNaN(utcDate.getTime())) return { date: '', time: '', timestamp: clean };

    // Convert to ET using Intl (handles EST/EDT automatically). Trade times are
    // ALWAYS stored in ET regardless of `userTimezone` (which is the user's
    // display preference for the economic calendar, not a storage axis).
    const etOptions = { timeZone: 'America/New_York' };
    const etYear = utcDate.toLocaleString('en-US', { ...etOptions, year: 'numeric' });
    const etMonth = utcDate.toLocaleString('en-US', { ...etOptions, month: '2-digit' });
    const etDay = utcDate.toLocaleString('en-US', { ...etOptions, day: '2-digit' });
    const etHour = utcDate.toLocaleString('en-US', { ...etOptions, hour: '2-digit', hour12: false });
    const etMinute = utcDate.toLocaleString('en-US', { ...etOptions, minute: '2-digit' });

    let hourStr = etHour.padStart(2, '0');
    const minStr = etMinute.padStart(2, '0');

    // Some browsers return hour "24" for midnight — fix the date and hour together
    let dateStr = `${etYear}-${etMonth}-${etDay}`;
    if (hourStr === '24') {
      hourStr = '00';
      // Midnight hour 24 belongs to the next calendar day
      const nextDay = new Date(dateStr + 'T12:00:00');
      nextDay.setDate(nextDay.getDate() + 1);
      const ny = nextDay.getFullYear();
      const nm = String(nextDay.getMonth() + 1).padStart(2, '0');
      const nd = String(nextDay.getDate()).padStart(2, '0');
      dateStr = `${ny}-${nm}-${nd}`;
    }
    const timeStr = `${hourStr}:${minStr}`;

    return { date: dateStr, time: timeStr, timestamp: utcDate.toISOString() };
  };

  for (let i = 1; i < lines.length; i++) {
    const values = [];
    let current = '';
    let inQuotes = false;
    for (const char of lines[i]) {
      if (char === '"') inQuotes = !inQuotes;
      else if (char === ',' && !inQuotes) { values.push(current.trim()); current = ''; }
      else current += char;
    }
    values.push(current.trim());

    if (values.length < 5) continue;

    const fill = {};
    headers.forEach((header, index) => {
      const value = values[index] || '';

      if (header === 'account') fill.accountId = value.trim();
      else if (header === 'ticket') fill.ticket = value.trim();
      else if (header === 'order id') fill.orderId = value.trim();
      else if (header === 'underlying') {
        // "Underlying" already has the clean base symbol (MNQ, NQ, ES, etc.)
        fill.symbol = value.trim().toUpperCase();
      }
      else if (header === 'action') {
        // BOT = buy, SLD = sell
        const act = value.trim().toUpperCase();
        fill.side = act === 'BOT' ? 'buy' : act === 'SLD' ? 'sell' : '';
      }
      else if (header === 'quantity') fill.quantity = parseInt(value) || 1;
      else if (header === 'price') fill.price = parseFloat(value.replace(/[$,]/g, '')) || 0;
      else if (header === 'time') {
        const parsed = parseMotiveWaveTime(value);
        fill.date = parsed.date;
        fill.time = parsed.time;
        fill.timestamp = parsed.timestamp;
      }
      else if (header === 'commission') fill.commission = Math.abs(parseFloat(value.replace(/[$,]/g, ''))) || 0;
      else if (header === 'fill id') fill.fillId = value.trim();
    });

    // Use ticket or orderId as fallback identifier since Fill Id is often empty
    fill.id = fill.fillId || fill.ticket || fill.orderId || `mw-${i}`;

    if (fill.price && fill.side && fill.symbol) fills.push(fill);
  }

  // Sort by timestamp
  fills.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

  // FIFO matching per account + symbol
  const trades = [];
  const openPositions = {};

  for (const fill of fills) {
    const key = `${fill.symbol}_${fill.accountId}`;
    if (!openPositions[key]) openPositions[key] = [];

    const positions = openPositions[key];
    // Preserve original quantity before matching consumes it (for commission pro-rating)
    if (fill.origQuantity == null) fill.origQuantity = fill.quantity;
    let remaining = fill.quantity;
    const oppositeSide = fill.side === 'buy' ? 'sell' : 'buy';

    // FIFO loop — match against ALL opposite positions until fill is consumed
    while (remaining > 0) {
      const oppositeIndex = positions.findIndex(p => p.side === oppositeSide);
      if (oppositeIndex === -1) break;

      const opp = positions[oppositeIndex];
      const matchQty = Math.min(remaining, opp.quantity);
      const isLong = opp.side === 'buy'; // Earlier fill was buy = long trade

      // Entry is always the earlier fill (opp) that opened the position,
      // exit is always the current fill that closes it — regardless of direction
      const entryFill = opp;
      const exitFill = fill;

      // Apply trading day boundary based on entry time (already in ET)
      const tradingDate = applyTradingDayBoundary(entryFill.date, entryFill.time);

      // Pro-rate commission based on matched vs original quantity
      const entryOrigQty = entryFill.origQuantity || entryFill.quantity;
      const exitOrigQty = exitFill.origQuantity || exitFill.quantity;
      const entryCommRate = (entryFill.commission || 0) * (matchQty / Math.max(entryOrigQty, 1));
      const exitCommRate = (exitFill.commission || 0) * (matchQty / Math.max(exitOrigQty, 1));

      trades.push({
        date: tradingDate,
        symbol: fill.symbol,
        entry: entryFill.price.toString(),
        exit: exitFill.price.toString(),
        quantity: matchQty.toString(),
        direction: isLong ? 'long' : 'short',
        entryTime: entryFill.time || '',
        exitTime: exitFill.time || '',
        entryTimeRaw: entryFill.timestamp || '',
        exitTimeRaw: exitFill.timestamp || '',
        accountId: fill.accountId || '',
        fillIndex: trades.length,
        importedCommission: entryCommRate + exitCommRate,
        setup: '', stopLoss: '', tags: [], mistakes: [], notes: ''
      });

      remaining -= matchQty;
      opp.quantity -= matchQty;
      if (opp.quantity <= 0) positions.splice(oppositeIndex, 1);
    }

    // Any remaining quantity becomes a new open position
    if (remaining > 0) {
      positions.push({ ...fill, quantity: remaining, origQuantity: fill.quantity });
    }
  }

  // Collect unmatched positions to warn the user
  const unmatched = [];
  for (const [_key, posArr] of Object.entries(openPositions)) {
    for (const pos of posArr) {
      unmatched.push({ symbol: pos.symbol, side: pos.side, quantity: pos.quantity, date: pos.date, price: pos.price });
    }
  }
  if (unmatched.length > 0) {
    const summary = unmatched.map(u => `${u.side.toUpperCase()} ${u.quantity} ${u.symbol} @ ${u.price} (${u.date})`).join(', ');
    console.warn(`[MotiveWave Import] ${unmatched.length} unmatched position(s) not imported: ${summary}`);
    // Attach to trades array so the UI can display a warning if needed
    trades._unmatchedPositions = unmatched;
  }

  return trades;
};


// Parse Sierra Charts TradesList.txt format - tab-separated, each row is a complete round-trip trade
const parseSierraCharts = (text, userTimezone = SESSION_TZ) => {
  const lines = text.trim().split('\n');
  if (lines.length < 2) return [];

  // Sierra Charts uses tab-separated values
  const headers = lines[0].split('\t').map(h => h.trim().toLowerCase());
  const trades = [];

  // Known futures base symbols (order matters - check longer names first)
  const knownSymbols = [
    'MNQ', 'MES', 'MYM', 'M2K', 'MCL', 'MGC', 'MBT', 'MET',
    'NQ', 'ES', 'YM', 'RTY', 'CL', 'GC', 'SI', 'HG', 'NG', 'ZB', 'ZN', 'ZF', 'ZT',
    'BTC', 'ETH', 'EUR', 'JPY', 'GBP', 'AUD', 'CAD', 'NZD', '6E', '6J', '6B', '6A', '6C',
    'UB', 'TN', 'HE', 'LE', 'ZC', 'ZS', 'ZW', 'KC', 'SB', 'CT', 'CC'
  ];

  // Parse Sierra Charts symbol format: [Sim]NQH26-CME (Sim1) -> NQ
  const parseSCSymbol = (rawSymbol) => {
    if (!rawSymbol) return '';
    // Strip [Sim] prefix
    let sym = rawSymbol.replace(/^\[Sim\]/i, '');
    // Strip (Sim1) or similar suffixes
    sym = sym.replace(/\s*\([^)]*\)\s*$/, '');
    // Strip exchange suffix (-CME, -NYMEX, -COMEX, etc.)
    sym = sym.replace(/-[A-Z]+$/, '');
    // Now we have something like NQH26 — strip futures month code + year
    // Month codes: F(Jan) G(Feb) H(Mar) J(Apr) K(May) M(Jun) N(Jul) Q(Aug) U(Sep) V(Oct) X(Nov) Z(Dec)
    // Try known symbols first (longest match)
    const upperSym = sym.toUpperCase();
    for (const known of knownSymbols) {
      if (upperSym.startsWith(known) && /^[FGHJKMNQUVXZ]\d{1,2}$/.test(upperSym.slice(known.length))) {
        return known;
      }
    }
    // Fallback: strip trailing month code + digits
    sym = upperSym.replace(/[FGHJKMNQUVXZ]\d{1,2}$/, '');
    return sym || rawSymbol.toUpperCase();
  };

  // Parse Sierra Charts datetime: "2026-01-01  19:20:00.186 BP" -> { date, time }
  const parseSCDateTime = (raw) => {
    if (!raw) return { date: '', time: '' };
    // Strip BP/EP suffix
    let clean = raw.replace(/\s+(BP|EP)\s*$/i, '').trim();
    // Split on whitespace (may have double spaces)
    const parts = clean.split(/\s+/);
    const datePart = parts[0] || ''; // YYYY-MM-DD
    const timePart = parts[1] || ''; // HH:MM:SS.mmm
    // Extract HH:MM from time
    const timeOnly = timePart.split('.')[0]; // strip milliseconds
    const [h, m] = (timeOnly || '').split(':');
    const timeFormatted = h && m ? `${h.padStart(2, '0')}:${m.padStart(2, '0')}` : '';
    return { date: datePart, time: timeFormatted };
  };

  // Build column index map
  const colIdx = {};
  headers.forEach((h, i) => { colIdx[h] = i; });

  for (let i = 1; i < lines.length; i++) {
    const values = lines[i].split('\t').map(v => v.trim());

    // Skip the total row (starts with empty symbol, contains "Total:")
    if (!values[0] && lines[i].includes('Total:')) continue;
    if (values.length < 5) continue;

    const getVal = (colName) => values[colIdx[colName]] || '';

    const rawSymbol = getVal('symbol');
    if (!rawSymbol) continue; // Skip empty rows

    const symbol = parseSCSymbol(rawSymbol);
    const tradeType = getVal('trade type').toLowerCase(); // "long" or "short"
    const entryDT = parseSCDateTime(getVal('entry datetime'));
    const exitDT = parseSCDateTime(getVal('exit datetime'));
    const entryPrice = getVal('entry price');
    const exitPrice = getVal('exit price');
    const quantity = getVal('trade quantity') || '1';

    // P&L - Sierra Charts uses "Profit/Loss (C)"
    let pnl = getVal('profit/loss (c)');
    // Handle FlatToFlat suffix: "2470.00 F" -> "2470.00"
    pnl = pnl.replace(/\s*F\s*$/, '');

    // MFE/MAE
    let mfe = getVal('max open profit (c)');
    let mae = getVal('max open loss (c)');  // These are negative values
    mfe = mfe.replace(/\s*F\s*$/, '');
    mae = mae.replace(/\s*F\s*$/, '');

    // Commission
    const commission = getVal('commission (c)');

    // Note field
    const notes = getVal('note') || '';

    // Account
    const account = getVal('account') || '';

    // Duration
    const duration = getVal('duration') || '';

    if (!symbol || !entryPrice || !exitPrice) continue;

    // Normalize wall-clock entry/exit to ET, then apply 6pm boundary
    const entryEt = normalizeToEt(entryDT.date, entryDT.time, userTimezone);
    const exitEt  = normalizeToEt(exitDT.date || entryDT.date, exitDT.time, userTimezone);
    let tradingDate = entryEt.date;
    if (entryEt.time) {
      tradingDate = applyTradingDayBoundary(entryEt.date, entryEt.time);
    }

    trades.push({
      date: tradingDate,
      symbol: symbol,
      entry: entryPrice,
      exit: exitPrice,
      quantity: quantity.replace(/[^0-9]/g, '') || '1',
      direction: tradeType === 'short' ? 'short' : 'long',
      entryTime: entryEt.time,
      exitTime: exitEt.time,
      accountId: account,
      ntProfit: pnl,
      mfe: mfe ? parseFloat(mfe) || null : null,
      mae: mae ? parseFloat(mae) || null : null,
      importedCommission: Math.abs(parseFloat(commission)) || 0,
      notes: notes,
      duration: duration,
      setup: '',
      stopLoss: '',
      tags: [],
      mistakes: []
    });
  }

  return trades;
};


// Detect CSV format and parse accordingly
const detectAndParseCSV = (text, forceSource = 'auto', fillsData = null, userTimezone = 'America/New_York') => {
  const lines = text.trim().split('\n');
  if (lines.length < 2) return [];

  const headers = lines[0].toLowerCase();
  // Split into individual column names for exact matching (avoids substring false positives)
  const headerCols = headers.split(',').map(h => h.trim().replace(/"/g, ''));

  // MotiveWave detection - CSV with Underlying, Action (BOT/SLD), Ticket columns
  const isMotiveWave = forceSource === 'motivewave' ||
    (forceSource === 'auto' && headerCols.includes('underlying') && headerCols.includes('action') && headerCols.includes('ticket'));

  if (isMotiveWave) {
    return parseMotiveWaveFills(text, userTimezone);
  }

  // Sierra Charts detection - tab-separated with unique column names
  const isSierraCharts = forceSource === 'sierra-charts' ||
    (forceSource === 'auto' && headers.includes('trade type') && headers.includes('entry datetime') && headers.includes('exit datetime'));

  if (isSierraCharts) {
    return parseSierraCharts(text, userTimezone);
  }

  const isTradovatePerformance = forceSource === 'tradovate-performance' ||
    (forceSource === 'auto' && headers.includes('buyfillid') && headers.includes('sellfillid') && headers.includes('pnl'));

  if (isTradovatePerformance) {
    return parseTradovatePerformance(text, fillsData, userTimezone);
  }

  const isTradovateFills = forceSource === 'tradovate-fills' ||
    (forceSource === 'auto' && (headers.includes('_action') || headers.includes('fill id') ||
      (headers.includes('b/s') && headers.includes('commission') && headers.includes('contract'))));

  if (isTradovateFills) {
    return parseTradovateFills(text, userTimezone);
  }

  return parseCSV(text, userTimezone);
};

// Group partial fills by Entry Time + Account + Symbol + Direction
const groupTradesByEntry = (trades, importAccountId) => {
  const groups = {};
  
  trades.forEach(trade => {
    const entryTimeKey = trade.entryTimeRaw || trade.date;
    const accountId = trade.accountId || importAccountId || 'no-account';
    const direction = trade.direction || 'long';
    const key = `${entryTimeKey}|${accountId}|${trade.symbol}|${direction}`;
    
    if (!groups[key]) {
      groups[key] = {
        entryTime: trade.entryTime || '',
        entryTimeRaw: trade.entryTimeRaw || '',
        exitTime: trade.exitTime || '',
        exitTimeRaw: trade.exitTimeRaw || '',
        date: trade.date,
        symbol: trade.symbol,
        direction,
        accountId: trade.accountId || '',
        setup: trade.setup,
        atmStrategy: trade.atm || '',
        entries: [],
        exits: [],
        totalPnL: 0,
        totalMAE: 0,
        totalMFE: 0,
        totalCommission: 0,
        notes: trade.notes || ''
      };
    }
    
    const group = groups[key];
    
    if (trade.exitTimeRaw && (!group.exitTimeRaw || trade.exitTimeRaw > group.exitTimeRaw)) {
      group.exitTime = trade.exitTime;
      group.exitTimeRaw = trade.exitTimeRaw;
    }
    
    const entryPrice = parseFloat(trade.entry);
    const exitPrice = parseFloat(trade.exit);
    const qty = parseInt(trade.quantity) || 1;
    
    const existingEntry = group.entries.find(e => e.price === entryPrice);
    if (existingEntry) existingEntry.quantity += qty;
    else group.entries.push({ quantity: qty, price: entryPrice });
    
    const existingExit = group.exits.find(e => e.price === exitPrice);
    if (existingExit) {
      existingExit.quantity += qty;
      // Update time if this exit is later (for accurate last exit time per price level)
      if (trade.exitTime && (!existingExit.time || trade.exitTimeRaw > existingExit.timeRaw)) {
        existingExit.time = trade.exitTime;
        existingExit.timeRaw = trade.exitTimeRaw;
      }
    }
    else group.exits.push({ quantity: qty, price: exitPrice, time: trade.exitTime || '', timeRaw: trade.exitTimeRaw || '' });
    
    if (trade.ntProfit && !isNaN(parseFloat(trade.ntProfit))) {
      group.totalPnL += parseFloat(trade.ntProfit);
    }
    
    if (trade.mae) group.totalMAE = Math.max(group.totalMAE, parseFloat(trade.mae) || 0);
    if (trade.mfe) group.totalMFE = Math.max(group.totalMFE, parseFloat(trade.mfe) || 0);
    if (trade.importedCommission && !isNaN(trade.importedCommission)) {
      group.totalCommission += trade.importedCommission;
    }
    
    if (trade.setup && trade.setup.length > (group.setup || '').length) {
      group.setup = trade.setup;
    }
    if (trade.atm && !group.atmStrategy) group.atmStrategy = trade.atm;
  });
  
  return Object.values(groups);
};


const ImportModal = ({
  darkMode,
  accounts,
  trades,
  setTrades,
  setAccounts,
  showImportModal,
  setShowImportModal,
  newAccountsFromImport,
  setNewAccountsFromImport,
  matchAtmToSetup,
  userTimezone = 'Eastern',
  // Optional — when supplied, imported trades auto-tag against any economic
  // events already saved for their respective dates (mirrors JournalView's
  // per-day effect, but as a one-shot pass at import time).
  journalEntries = {}
}) => {
  // Resolve IANA timezone for UTC conversion
  const ianaTimezone = getIANATimezone(userTimezone);
  // Local state for import modal
  const [importData, setImportData] = useState('');
  const [importPreview, setImportPreview] = useState([]);
  const [importAccountId, setImportAccountId] = useState('');
  const [importSource, setImportSource] = useState('ninjatrader');
  const [tradovateFillsData, setTradovateFillsData] = useState(null);
  const [groupByEntryTime, setGroupByEntryTime] = useState(true);
  const [duplicateCount, setDuplicateCount] = useState(0);
  const [enrichCount, setEnrichCount] = useState(0);

  // Import-type toggle: 'trades' is the legacy flow (CSV → trade rows);
  // 'chartData' uploads OHLCV bar files (NinjaTrader chart export, etc.) into
  // a platform-specific folder so the trade chart can render real candles.
  const [importType, setImportType] = useState('trades');

  // Chart-data import state. Populated when the user picks a file in
  // chartData mode; cleared on close or when they switch source/type.
  // Shape: { fileName, rawText, bars, days: [...], firstTime, lastTime, error }
  const [chartFileMeta, setChartFileMeta] = useState(null);
  // 'idle' | 'saving' | 'saved' | 'error' | 'exists' — drives the Save button
  // label + the "file already exists, overwrite?" prompt.
  const [chartSaveState, setChartSaveState] = useState('idle');
  const [chartSaveError, setChartSaveError] = useState('');

  // Helper to build trade signature for duplicate detection
  const tradeSig = (trade) => {
    const entryRounded = typeof trade.entry === 'number' ? trade.entry.toFixed(2) : parseFloat(trade.entry).toFixed(2);
    const exitVal = Array.isArray(trade.exits) ? trade.exits.map(e => `${e.price}x${e.quantity}`).join('+') : (typeof trade.exit === 'number' ? trade.exit.toFixed(2) : parseFloat(trade.exit || 0).toFixed(2));
    // Include fillIndex when present (MotiveWave FIFO) to distinguish identical partial fills
    const fillIdx = trade.fillIndex != null ? `|fi:${trade.fillIndex}` : '';
    return `${trade.date}|${trade.symbol}|${trade.direction}|${entryRounded}|${exitVal}|${trade.entryTime || ''}|${trade.quantity}|${trade.accountId || ''}${fillIdx}`;
  };

  // Helper to count duplicates and enrichable trades against existing trades
  const countDuplicates = (parsedTrades) => {
    const existingBySignature = {};
    trades.forEach(trade => {
      existingBySignature[tradeSig(trade)] = trade;
    });

    let dupeCount = 0;
    let enrichCount = 0;
    parsedTrades.forEach(trade => {
      const sig = tradeSig(trade);
      const existing = existingBySignature[sig];
      if (existing) {
        // Check if import has data that existing trade is missing
        const hasMae = trade.mae && !isNaN(parseFloat(trade.mae));
        const hasMfe = trade.mfe && !isNaN(parseFloat(trade.mfe));
        const hasCommission = trade.importedCommission && !existing.importedCommission;
        if ((hasMae && !existing.mae) || (hasMfe && !existing.mfe) || hasCommission) {
          enrichCount++;
        } else {
          dupeCount++;
        }
      }
    });
    return { dupeCount, enrichCount };
  };

  const [unmatchedWarning, setUnmatchedWarning] = useState(null);

  // Helper to set preview and calculate duplicates
  const updatePreview = (parsed) => {
    setImportPreview(parsed);
    const { dupeCount, enrichCount: enrich } = countDuplicates(parsed);
    setDuplicateCount(dupeCount);
    setEnrichCount(enrich);
    // Surface unmatched position warnings from MotiveWave/FIFO parsers
    setUnmatchedWarning(parsed._unmatchedPositions || null);
  };

  // Detect new accounts from import data
  const detectNewAccountsFromImport = (parsedTrades) => {
    const importAccountIds = [...new Set(parsedTrades.map(t => t.accountId).filter(Boolean))];
    const existingAccountIds = accounts.map(a => a.id);
    const newAccounts = importAccountIds
      .filter(id => !existingAccountIds.includes(id))
      .map(id => {
        const sampleTrade = parsedTrades.find(t => t.accountId === id);
        let broker = 'Unknown';
        let commissionPerSide = 0.61;
        
        if (id.startsWith('BX')) { broker = 'Bulenox'; commissionPerSide = 0.61; }
        else if (id.startsWith('AP') || id.includes('APEX')) { broker = 'Apex'; commissionPerSide = 0.52; }
        else if (id.startsWith('TS') || id.includes('TopStep')) { broker = 'TopStep'; commissionPerSide = 0.52; }
        
        if (sampleTrade?.importedCommission && sampleTrade?.quantity) {
          const qty = parseInt(sampleTrade.quantity) || 1;
          const calculatedPerSide = sampleTrade.importedCommission / 2 / qty;
          if (calculatedPerSide > 0) commissionPerSide = Math.round(calculatedPerSide * 100) / 100;
        }
        
        return {
          id, name: id, broker, type: 'Evaluation', status: 'Active',
          defaultSymbol: sampleTrade?.symbol || 'MNQ', strategy: '',
          startingBalance: 50000, drawdownType: 'eodTrailing',
          drawdownThreshold: '', commissionPerSide, balanceAdjustment: 0,
          balanceLastSynced: null, selected: true
        };
      });
    setNewAccountsFromImport(newAccounts);
  };

  const handleImportChange = (e) => {
    const text = e.target.value;
    setImportData(text);
    
    if (text.trim()) {
      const parsed = detectAndParseCSV(text, importSource, tradovateFillsData, ianaTimezone);
      updatePreview(parsed);
      detectNewAccountsFromImport(parsed);
    } else {
      setImportPreview([]);
      setDuplicateCount(0);
      setNewAccountsFromImport([]);
    }
  };

  const handleFileImport = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const text = event.target.result;
        setImportData(text);
        const parsed = detectAndParseCSV(text, importSource, tradovateFillsData, ianaTimezone);
        updatePreview(parsed);
        detectNewAccountsFromImport(parsed);
      };
      // FileReader error path was silent before — corrupt/unreadable uploads
      // looked like nothing happened. Log loudly + clear preview so the
      // upload zone shows its empty/idle state and the user knows to retry.
      reader.onerror = () => {
        console.error(`[Import] Failed to read trades file "${file.name}":`, reader.error);
        setImportData('');
        setImportPreview([]);
      };
      reader.readAsText(file);
    }
  };

  const handleFillsFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setTradovateFillsData(event.target.result);
        if (importData.trim()) {
          const parsed = detectAndParseCSV(importData, importSource, event.target.result, ianaTimezone);
          updatePreview(parsed);
          detectNewAccountsFromImport(parsed);
        }
      };
      reader.onerror = () => {
        console.error(`[Import] Failed to read fills file "${file.name}":`, reader.error);
        // Clear so the optional-fills banner returns to its "Upload Fills.csv"
        // default state and the user knows to retry.
        setTradovateFillsData(null);
      };
      reader.readAsText(file);
    }
  };

  // ── Chart-data import (Phase C v0) ─────────────────────────────────────
  // For NinjaTrader: parse the uploaded chart-export file with the existing
  // bar parser, summarize coverage, and (on confirm) write it into the
  // JournifiBars folder where NinjaTraderBarSource will find it.
  //
  // For Tradovate / Sierra Charts / MotiveWave: not supported yet — we don't
  // have sample exports to validate against, so the UI shows "Coming soon"
  // and disables the file input.
  const platformOf = (src) => (src || '').split('-')[0];
  const isChartDataSupported = (src) => platformOf(src) === 'ninjatrader';

  const buildChartMeta = (fileName, rawText) => {
    const { bars, parsed, skipped, error } = parseCsvToBars(rawText, { label: fileName });
    if (error || bars.length === 0) {
      return { fileName, rawText, bars: [], error: error || 'No bars parsed', parsed, skipped };
    }
    // Group by trading day (6pm ET boundary) so the user sees session coverage,
    // not raw calendar days — matches how the chart will key into this file.
    const dayCounts = {};
    for (const b of bars) {
      const td = getTradingDay(b.time * 1000);
      if (!td) continue;
      dayCounts[td] = (dayCounts[td] || 0) + 1;
    }
    const days = Object.keys(dayCounts).sort();
    return {
      fileName,
      rawText,
      bars,
      parsed,
      skipped,
      days,
      dayCounts,
      firstTime: bars[0].time,
      lastTime: bars[bars.length - 1].time,
      error: null,
    };
  };

  const handleChartFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return; // user cancelled native picker — silent is correct here
    setChartSaveState('idle');
    setChartSaveError('');
    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      setChartFileMeta(buildChartMeta(file.name, text));
    };
    reader.onerror = () => {
      console.error(`[Import] Failed to read chart file "${file.name}":`, reader.error);
      // Surface to the user via the existing chart-data error UI: the "couldn't
      // parse this file" red banner inside the preview section.
      setChartFileMeta({
        fileName: file.name,
        rawText: '',
        bars: [],
        error: `Could not read file (${reader.error?.message || 'unknown read error'})`,
      });
    };
    reader.readAsText(file);
  };

  const handleChartSave = async (overwrite = false) => {
    if (!chartFileMeta || chartFileMeta.error || chartFileMeta.bars.length === 0) return;
    if (!window.electronAPI?.ntbars?.writeFile) {
      setChartSaveState('error');
      setChartSaveError('Chart-data write only works in the desktop app.');
      return;
    }
    setChartSaveState('saving');
    setChartSaveError('');
    try {
      const res = await window.electronAPI.ntbars.writeFile(
        chartFileMeta.fileName,
        chartFileMeta.rawText,
        { overwrite }
      );
      if (res?.success) {
        setChartSaveState('saved');
        return;
      }
      if (res?.reason === 'exists') {
        setChartSaveState('exists');
        return;
      }
      setChartSaveState('error');
      setChartSaveError(res?.error || 'Unknown error');
    } catch (err) {
      setChartSaveState('error');
      setChartSaveError(err?.message || String(err));
    }
  };

  const handleSourceChange = (newSource) => {
    setImportSource(newSource);
    // MotiveWave and Tradovate fills already do FIFO matching internally — disable grouping
    if (newSource === 'motivewave' || newSource === 'tradovate-fills') {
      setGroupByEntryTime(false);
    } else if (newSource === 'ninjatrader') {
      setGroupByEntryTime(true);
    }
    if (importData.trim()) {
      const parsed = detectAndParseCSV(importData, newSource, tradovateFillsData, ianaTimezone);
      updatePreview(parsed);
      detectNewAccountsFromImport(parsed);
    }
  };


  const confirmImport = () => {
    let tradesToProcess = importPreview;

    // Skip grouping for sources that already do FIFO matching internally
    const skipGrouping = importSource === 'motivewave' || importSource === 'tradovate-fills';
    if (groupByEntryTime && !skipGrouping) {
      const groupedTrades = groupTradesByEntry(importPreview, importAccountId);
      
      tradesToProcess = groupedTrades.map((group, index) => {
        const totalEntryQty = group.entries.reduce((sum, e) => sum + e.quantity, 0);
        const weightedEntry = group.entries.reduce((sum, e) => sum + (e.price * e.quantity), 0) / totalEntryQty;
        const tradeAccountId = group.accountId || importAccountId || '';
        const direction = group.direction || 'long';
        
        // Parse ATM name to extract stop loss points
        let stopLoss = 0;
        let stopLossPoints = null;
        if (group.atmStrategy) {
          const atmParsed = parseAtmTemplateName(group.atmStrategy);
          if (atmParsed.stopLossPoints) {
            stopLossPoints = atmParsed.stopLossPoints;
            // Calculate stop loss level from points
            if (direction === 'long') {
              stopLoss = weightedEntry - stopLossPoints;
            } else {
              stopLoss = weightedEntry + stopLossPoints;
            }
          }
        }
        
        // Try to match ATM to a setup if no setup provided
        let setup = group.setup || '';
        if (!setup && group.atmStrategy && matchAtmToSetup) {
          const matchedSetup = matchAtmToSetup(group.atmStrategy);
          if (matchedSetup) {
            setup = matchedSetup;
          }
        }
        if (!setup) {
          setup = 'Imported';
        }
        
        return {
          id: Date.now() + index,
          date: group.date,
          symbol: group.symbol.toUpperCase(),
          setup: setup,
          atmStrategy: group.atmStrategy || '',
          entry: weightedEntry,
          exits: group.exits.sort((a, b) => a.price - b.price).map(e => ({ quantity: e.quantity, price: e.price, time: e.time || '' })),
          stopLoss: stopLoss,
          stopLossPoints: stopLossPoints,
          useStopLossPoints: stopLossPoints !== null,
          quantity: totalEntryQty,
          pnl: group.totalPnL,
          rMultiple: 0,
          tags: [],
          mistakes: [],
          notes: group.entries.length > 1 
            ? `Scaled in: ${group.entries.map(e => `${e.quantity}@${e.price}`).join(', ')}` 
            : '',
          accountId: tradeAccountId,
          direction: group.direction,
          mae: group.totalMAE || null,
          mfe: group.totalMFE || null,
          importedCommission: group.totalCommission || null,
          scaledIn: group.entries.length > 1,
          entryLevels: group.entries.length > 1 ? group.entries : null,
          entryTime: group.entryTime || null,
          exitTime: group.exitTime || null
        };
      });
    } else {
      tradesToProcess = importPreview.map((trade, index) => {
        const symbol = trade.symbol.toUpperCase();
        const entry = parseFloat(trade.entry);
        const exit = parseFloat(trade.exit);
        const quantity = parseInt(trade.quantity) || 1;
        let stopLoss = parseFloat(trade.stopLoss) || 0;
        let stopLossPoints = null;
        const direction = trade.direction || 'long';
        
        // Parse ATM name to extract stop loss points if not already set
        if (trade.atm && stopLoss === 0) {
          const atmParsed = parseAtmTemplateName(trade.atm);
          if (atmParsed.stopLossPoints) {
            stopLossPoints = atmParsed.stopLossPoints;
            // Calculate stop loss level from points
            if (direction === 'long') {
              stopLoss = entry - stopLossPoints;
            } else {
              stopLoss = entry + stopLossPoints;
            }
          }
        }
        
        let calculatedPnL = 0;
        const _importSpec = getSpec(symbol);
        if (trade.ntProfit && !isNaN(parseFloat(trade.ntProfit))) {
          calculatedPnL = parseFloat(trade.ntProfit);
        } else if (_importSpec) {
          const spec = _importSpec;
          const points = direction === 'short' ? (entry - exit) : (exit - entry);
          const ticks = safeTicks(points, spec.tickSize);
          calculatedPnL = ticks * spec.tickValue * quantity;
        } else {
          const points = direction === 'short' ? (entry - exit) : (exit - entry);
          calculatedPnL = points * quantity;
        }
        
        // Try to match ATM to a setup if no setup provided
        let setup = trade.setup || '';
        if (!setup && trade.atm && matchAtmToSetup) {
          const matchedSetup = matchAtmToSetup(trade.atm);
          if (matchedSetup) {
            setup = matchedSetup;
          }
        }
        if (!setup) {
          setup = 'Imported';
        }
        
        return {
          id: Date.now() + index,
          date: trade.date,
          symbol: symbol,
          setup: setup,
          atmStrategy: trade.atm || '',
          entry: entry,
          exits: [{ quantity: quantity, price: exit, time: trade.exitTime || '' }],
          stopLoss: stopLoss,
          stopLossPoints: stopLossPoints,
          useStopLossPoints: stopLossPoints !== null,
          quantity: quantity,
          pnl: calculatedPnL,
          rMultiple: 0,
          tags: [],
          mistakes: [],
          notes: trade.notes || '',
          accountId: trade.accountId || importAccountId || '',
          direction: direction,
          mae: trade.mae ? parseFloat(trade.mae) : null,
          mfe: trade.mfe ? parseFloat(trade.mfe) : null,
          importedCommission: trade.importedCommission || null,
          entryTime: trade.entryTime || null,
          exitTime: trade.exitTime || null,
          journaled: false  // Requires user to open and save to mark as journaled
        };
      });
    }
    
    // Duplicate detection + enrichment - build signature→trade map from existing trades
    const existingBySignature = {};
    trades.forEach(trade => {
      existingBySignature[tradeSig(trade)] = trade;
    });

    // Filter out duplicates, enrich existing trades with missing data (MAE/MFE/commission)
    const uniqueTrades = [];
    const enrichedIds = new Set();
    const importSigs = new Set();

    tradesToProcess.forEach(trade => {
      const sig = tradeSig(trade);
      if (importSigs.has(sig)) return; // Skip duplicates within import batch
      importSigs.add(sig);

      const existing = existingBySignature[sig];
      if (existing) {
        // Enrich existing trade with data from import that it's missing
        let enriched = false;
        if (trade.mae && !isNaN(parseFloat(trade.mae)) && !existing.mae) {
          existing.mae = parseFloat(trade.mae);
          enriched = true;
        }
        if (trade.mfe && !isNaN(parseFloat(trade.mfe)) && !existing.mfe) {
          existing.mfe = parseFloat(trade.mfe);
          enriched = true;
        }
        if (trade.importedCommission && !existing.importedCommission) {
          existing.importedCommission = trade.importedCommission;
          enriched = true;
        }
        if (enriched) enrichedIds.add(existing.id);
      } else {
        uniqueTrades.push(trade);
      }
    });

    // Auto-tag the new trades against any economic events already saved for
    // their dates. Idempotent — never re-adds a tag the trade already has,
    // safe to run alongside JournalView's per-day auto-tag effect.
    const { tagged: taggedNewTrades, stats: tagStats } = autoTagImportedTrades(
      uniqueTrades,
      journalEntries
    );

    // Build updated trades array: patch enriched existing trades + append new ones
    const updatedTrades = enrichedIds.size > 0
      ? trades.map(t => enrichedIds.has(t.id) ? { ...t } : t)
      : trades;
    setTrades([...updatedTrades, ...taggedNewTrades]);

    // Quiet enhancement — log to console for visibility but no toast.
    // Tags appearing on imported trades is the expected behavior (matches
    // JournalView's existing per-day auto-tag effect), so a popup would
    // be more noise than signal.
    if (tagStats.totalTagged > 0) {
      console.log(`[Import] Auto-tagged ${tagStats.totalTagged} trade(s) against ${tagStats.eventsApplied} event(s) on their respective dates.`);
    }

    if (newAccountsFromImport.length > 0) {
      const accountsToCreate = newAccountsFromImport
        .filter(acc => acc.selected)
        .map(acc => ({
          id: acc.id, name: acc.name, broker: acc.broker,
          type: acc.type, status: acc.status, defaultSymbol: acc.defaultSymbol,
          strategy: acc.strategy || '', startingBalance: acc.startingBalance,
          dailyLossLimit: '', maxDrawdown: '', drawdownType: 'eodTrailing',
          drawdownThreshold: '', commissionPerSide: 0.61,
          balanceAdjustment: 0, balanceLastSynced: null
        }));
      
      if (accountsToCreate.length > 0) {
        setAccounts([...accounts, ...accountsToCreate]);
      }
    }
    
    closeModal();
  };

  const closeModal = () => {
    setShowImportModal(false);
    setImportData('');
    setImportPreview([]);
    setImportAccountId('');
    setGroupByEntryTime(true);
    setNewAccountsFromImport([]);
    setTradovateFillsData(null);
    setImportSource('ninjatrader');
    setDuplicateCount(0);
    setImportType('trades');
    setChartFileMeta(null);
    setChartSaveState('idle');
    setChartSaveError('');
  };


  if (!showImportModal) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className={`rounded-2xl p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto transition-colors duration-300 ${
        darkMode ? 'bg-gray-950/90 backdrop-blur-2xl border border-white/10' : 'bg-white'
      }`}>
        <div className="flex justify-between items-center mb-4">
          <h2 className={`text-xl font-bold transition-colors duration-300 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            {importType === 'chartData' ? 'Import Chart Data' : 'Import Trades'}
          </h2>
          <button
            onClick={closeModal}
            className={`p-2 rounded-lg transition-colors duration-300 ${
              darkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-600'
            }`}
          >
            <X size={20} />
          </button>
        </div>

        {/* Import-type segmented toggle. Trades is the legacy CSV→trade flow;
            Chart Data uploads OHLCV bar files (NinjaTrader chart export, etc.)
            so trade-card charts can render real candles instead of sample
            data. Switching resets the file/preview state for the new mode. */}
        <div className={`mb-6 inline-flex rounded-lg p-1 ${darkMode ? 'bg-gray-800/60' : 'bg-gray-100'}`}>
          <button
            type="button"
            onClick={() => setImportType('trades')}
            className={`flex items-center gap-1.5 px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
              importType === 'trades'
                ? (darkMode ? 'bg-blue-500/30 text-blue-100' : 'bg-white text-blue-700 shadow-sm')
                : (darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700')
            }`}
          >
            <Plus size={13} />
            Trades
          </button>
          <button
            type="button"
            onClick={() => setImportType('chartData')}
            className={`flex items-center gap-1.5 px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
              importType === 'chartData'
                ? (darkMode ? 'bg-purple-500/30 text-purple-100' : 'bg-white text-purple-700 shadow-sm')
                : (darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700')
            }`}
          >
            <BarChart3 size={13} />
            Chart Data
          </button>
        </div>

        <div className="mb-4">
          <div className={`text-sm mb-4 transition-colors duration-300 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            {importType === 'trades' ? (
              <p className="mb-2">Import trades from CSV/TXT. Supports NinjaTrader, Tradovate, Sierra Charts, and MotiveWave exports.</p>
            ) : (
              <p className="mb-2">Import OHLCV bar exports so trade charts show real candles. Currently supports NinjaTrader (Tools → Export → Historical Data, Interval = Minute, Data Type = Last).</p>
            )}
          </div>
          
          {/* Import Source Selector */}
          <div className="mb-4">
            <label className={`block text-sm font-medium mb-2 transition-colors duration-300 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
              Import Source:
            </label>
            <GlassSelect
              value={importSource}
              onChange={handleSourceChange}
              options={[
                { value: 'ninjatrader', label: 'NinjaTrader' },
                { value: 'tradovate-performance', label: 'Tradovate' },
                { value: 'sierra-charts', label: 'Sierra Charts' },
                { value: 'motivewave', label: 'MotiveWave' },
              ]}
              darkMode={darkMode}
              className="w-full"
            />
            <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              {importSource === 'ninjatrader' && "NinjaTrader Trade Performance - 'Trades' Export"}
              {importSource === 'tradovate-performance' && 'Tradovate Performance.csv export'}
              {importSource === 'sierra-charts' && 'Sierra Charts Trade Activity Log - Trades List export'}
              {importSource === 'motivewave' && "MotiveWave Trade History - Executions Export (.csv)"}
            </p>
          </div>
          
          {/* File Upload (trades mode) */}
          {importType === 'trades' && (
          <div className="flex gap-4 mb-4">
            <label className={`flex-1 px-4 py-2 border-2 border-dashed rounded-lg cursor-pointer transition-colors duration-300 ${
              darkMode
                ? 'border-gray-600 hover:border-gray-500 bg-gray-700'
                : 'border-gray-300 hover:border-gray-400 bg-gray-50'
            }`}>
              <input type="file" accept=".csv,.txt" onChange={handleFileImport} className="hidden" />
              <div className="text-center">
                <Plus size={24} className={`mx-auto mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  {importSource === 'tradovate-performance' && 'Click to upload Tradovate Performance CSV file'}
                  {importSource === 'sierra-charts' && 'Click to upload Sierra Charts TradesList.txt file'}
                  {importSource === 'motivewave' && 'Click to upload MotiveWave Executions CSV file'}
                  {importSource === 'ninjatrader' && 'Click to upload NinjaTrader Trades CSV file'}
                </span>
              </div>
            </label>
          </div>
          )}

          {/* Paste Area (trades mode only — chart-data exports are too large to paste) */}
          {importType === 'trades' && (
          <div className="mb-4">
            <label className={`block text-sm font-medium mb-2 transition-colors duration-300 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
              Or paste trade data:
            </label>
            <textarea
              value={importData}
              onChange={handleImportChange}
              placeholder="date,symbol,entry,exit,stop loss,quantity,setup,notes"
              rows={6}
              className="w-full input-modern font-mono placeholder:text-center resize-none"
            />
          </div>
          )}

          {/* Chart-data branch — file upload + preview stats + save. Supported
              for NinjaTrader; "Coming soon" panel for the other platforms
              until we have sample exports to validate against. */}
          {importType === 'chartData' && (
            isChartDataSupported(importSource) ? (
              <div className="mb-4">
                <label className={`flex-1 block px-4 py-3 border-2 border-dashed rounded-lg cursor-pointer transition-colors duration-300 ${
                  darkMode
                    ? 'border-purple-500/40 hover:border-purple-400/70 bg-purple-500/[0.04]'
                    : 'border-purple-300 hover:border-purple-400 bg-purple-50/50'
                }`}>
                  <input type="file" accept=".csv,.txt" onChange={handleChartFileUpload} className="hidden" />
                  <div className="text-center">
                    <BarChart3 size={24} className={`mx-auto mb-2 ${darkMode ? 'text-purple-300' : 'text-purple-500'}`} />
                    <span className={`text-sm ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                      {chartFileMeta?.fileName
                        ? <>Selected: <span className="font-mono">{chartFileMeta.fileName}</span> — click to choose a different file</>
                        : 'Click to upload a NinjaTrader chart export (.csv or .txt)'}
                    </span>
                    <p className={`text-[11px] mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      In NinjaTrader: Tools → Export → Historical Data… (Interval: Minute, Data Type: Last)
                    </p>
                  </div>
                </label>

                {/* Preview stats — only when we have a parsed file */}
                {chartFileMeta && (
                  <div className={`mt-4 p-3 rounded-lg border ${
                    chartFileMeta.error
                      ? (darkMode ? 'bg-red-500/10 border-red-400/40' : 'bg-red-50 border-red-200')
                      : (darkMode ? 'bg-gray-800/60 border-gray-600' : 'bg-gray-50 border-gray-200')
                  }`}>
                    {chartFileMeta.error ? (
                      <p className={`text-sm ${darkMode ? 'text-red-300' : 'text-red-700'}`}>
                        Couldn&apos;t parse this file: {chartFileMeta.error}
                      </p>
                    ) : (
                      <>
                        <div className="grid grid-cols-3 gap-3 text-sm">
                          <div>
                            <div className={`text-[10px] uppercase tracking-wider ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Bars parsed</div>
                            <div className={`font-mono ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>{chartFileMeta.parsed.toLocaleString()}</div>
                          </div>
                          <div>
                            <div className={`text-[10px] uppercase tracking-wider ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Trading days</div>
                            <div className={`font-mono ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>{chartFileMeta.days?.length ?? 0}</div>
                          </div>
                          <div>
                            <div className={`text-[10px] uppercase tracking-wider ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Coverage</div>
                            <div className={`font-mono text-xs ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                              {chartFileMeta.days?.length > 0
                                ? `${chartFileMeta.days[0]} → ${chartFileMeta.days[chartFileMeta.days.length - 1]}`
                                : '—'}
                            </div>
                          </div>
                        </div>
                        {chartFileMeta.skipped > 0 && (
                          <p className={`mt-2 text-xs ${darkMode ? 'text-amber-300' : 'text-amber-700'}`}>
                            {chartFileMeta.skipped} row{chartFileMeta.skipped !== 1 ? 's' : ''} skipped (malformed or empty)
                          </p>
                        )}
                        <p className={`mt-2 text-[11px] ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          Will save to <code className="font-mono">C:\NinjaTrader8Data\JournifiBars\{chartFileMeta.fileName}</code>.
                          The chart picks it up automatically for any trade in the covered date range.
                        </p>
                      </>
                    )}
                  </div>
                )}

                {/* Status banners — error / overwrite confirm / saved */}
                {chartSaveState === 'error' && (
                  <div className={`mt-2 p-2 rounded text-sm ${darkMode ? 'bg-red-500/10 text-red-300 border border-red-400/30' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                    Save failed: {chartSaveError}
                  </div>
                )}
                {chartSaveState === 'exists' && (
                  <div className={`mt-2 p-3 rounded text-sm flex items-center justify-between gap-3 ${
                    darkMode ? 'bg-amber-500/10 text-amber-200 border border-amber-400/30' : 'bg-amber-50 text-amber-800 border border-amber-200'
                  }`}>
                    <span>A file with this name already exists. Overwrite it?</span>
                    <button
                      type="button"
                      onClick={() => handleChartSave(true)}
                      className={`px-3 py-1 rounded text-xs font-medium ${
                        darkMode ? 'bg-amber-500/30 hover:bg-amber-500/40 text-amber-100' : 'bg-amber-200 hover:bg-amber-300 text-amber-900'
                      }`}
                    >
                      Overwrite
                    </button>
                  </div>
                )}
                {chartSaveState === 'saved' && (
                  <div className={`mt-2 p-2 rounded text-sm ${darkMode ? 'bg-green-500/10 text-green-300 border border-green-400/30' : 'bg-green-50 text-green-700 border border-green-200'}`}>
                    Saved. Open any trade in this date range to see the chart populate.
                  </div>
                )}
              </div>
            ) : (
              <div className={`mb-4 p-4 rounded-lg border-2 border-dashed text-center ${
                darkMode ? 'border-gray-700 bg-gray-800/40' : 'border-gray-300 bg-gray-50'
              }`}>
                <Lock size={20} className={`mx-auto mb-2 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                <p className={`text-sm font-medium ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                  Coming soon
                </p>
                <p className={`text-xs mt-1 max-w-sm mx-auto ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  We need a sample {importSource === 'tradovate-performance' ? 'Tradovate' : importSource.charAt(0).toUpperCase() + importSource.slice(1).replace('-', ' ')} chart export to validate the format. Switch to NinjaTrader to import bars today, or share an export so we can add support.
                </p>
              </div>
            )
          )}

          {/* Optional Fills.csv upload for Tradovate Performance */}
          {importType === 'trades' && importSource === 'tradovate-performance' && (
            <div className={`mb-4 p-3 rounded-lg border ${darkMode ? 'bg-gray-800/50 border-gray-700' : 'bg-blue-50 border-blue-200'}`}>
              <label className={`block text-sm font-medium mb-2 transition-colors duration-300 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                Optional: Add Fills.csv for commission & account data
              </label>
              <div className="flex items-center gap-3">
                <label className={`flex-1 px-3 py-2 border border-dashed rounded-lg cursor-pointer transition-colors duration-300 text-center ${
                  darkMode 
                    ? 'border-gray-600 hover:border-gray-500 bg-gray-700' 
                    : 'border-blue-300 hover:border-blue-400 bg-white'
                }`}>
                  <input type="file" accept=".csv" onChange={handleFillsFileUpload} className="hidden" />
                  <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    {tradovateFillsData ? '✓ Fills.csv loaded' : 'Upload Fills.csv (optional)'}
                  </span>
                </label>
                {tradovateFillsData && (
                  <button
                    onClick={() => {
                      setTradovateFillsData(null);
                      if (importData.trim()) {
                        const parsed = detectAndParseCSV(importData, importSource, null, ianaTimezone);
                        updatePreview(parsed);
                        detectNewAccountsFromImport(parsed);
                      }
                    }}
                    className={`px-3 py-2 rounded-lg text-sm ${
                      darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                    }`}
                  >
                    Remove
                  </button>
                )}
              </div>
              <p className={`text-xs mt-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Adds commission amounts and account ID to your imported trades
              </p>
            </div>
          )}

          {/* Preview Table (trades mode only) */}
          {importType === 'trades' && importPreview.length > 0 && (
            <div className="mb-4">
              <h3 className={`text-sm font-medium mb-2 transition-colors duration-300 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                Preview ({importPreview.length} trades found):
              </h3>
              <div className={`overflow-x-auto border rounded-lg ${darkMode ? 'border-gray-600' : 'border-gray-300'}`}>
                <table className="w-full text-sm">
                  <thead>
                    <tr className={`border-b ${darkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
                      <th className={`text-left py-2 px-3 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>Date</th>
                      <th className={`text-left py-2 px-3 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>Account</th>
                      <th className={`text-left py-2 px-3 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>Symbol</th>
                      <th className={`text-left py-2 px-3 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>Dir</th>
                      <th className={`text-right py-2 px-3 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>Entry</th>
                      <th className={`text-right py-2 px-3 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>Exit</th>
                      <th className={`text-right py-2 px-3 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>Qty</th>
                      <th className={`text-right py-2 px-3 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>P&L</th>
                      <th className={`text-right py-2 px-3 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>Comm</th>
                      <th className={`text-left py-2 px-3 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>Setup</th>
                    </tr>
                  </thead>
                  <tbody>
                    {importPreview.slice(0, 10).map((trade, index) => (
                      <tr key={index} className={`border-b ${darkMode ? 'border-gray-700' : 'border-gray-100'}`}>
                        <td className={`py-2 px-3 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{trade.date}</td>
                        <td className={`py-2 px-3 text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{trade.accountId || '-'}</td>
                        <td className={`py-2 px-3 font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{trade.symbol}</td>
                        <td className={`py-2 px-3 ${trade.direction === 'short' ? 'text-red-400' : 'text-green-400'}`}>
                          {trade.direction === 'short' ? 'S' : 'L'}
                        </td>
                        <td className={`py-2 px-3 text-right ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{trade.entry}</td>
                        <td className={`py-2 px-3 text-right ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{trade.exit}</td>
                        <td className={`py-2 px-3 text-right ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{trade.quantity}</td>
                        <td className={`py-2 px-3 text-right font-medium ${
                          trade.ntProfit && parseFloat(trade.ntProfit) >= 0 ? 'text-green-500' : 'text-red-500'
                        }`}>
                          {trade.ntProfit ? `$${parseFloat(trade.ntProfit).toFixed(2)}` : '-'}
                        </td>
                        <td className={`py-2 px-3 text-right text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          {trade.importedCommission ? `$${trade.importedCommission.toFixed(2)}` : '-'}
                        </td>
                        <td className={`py-2 px-3 text-xs truncate max-w-[150px] ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          {trade.setup || 'Imported'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {importPreview.length > 10 && (
                  <div className={`text-center py-2 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    ... and {importPreview.length - 10} more rows
                  </div>
                )}
              </div>
              
              {/* Import Summary Stats */}
              <div className={`mt-3 p-3 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                <div className="flex flex-wrap gap-4 text-xs">
                  <div>
                    <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>Date Range: </span>
                    <span className={darkMode ? 'text-white' : 'text-gray-900'}>
                      {(() => {
                        const dates = importPreview.map(t => t.date).sort();
                        return dates.length > 0 ? `${dates[0]} to ${dates[dates.length - 1]}` : '-';
                      })()}
                    </span>
                  </div>
                  <div>
                    <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>Symbols: </span>
                    <span className={darkMode ? 'text-white' : 'text-gray-900'}>
                      {[...new Set(importPreview.map(t => t.symbol))].join(', ')}
                    </span>
                  </div>
                  <div>
                    <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>Accounts: </span>
                    <span className={darkMode ? 'text-white' : 'text-gray-900'}>
                      {(() => {
                        const uniqueAccounts = [...new Set(importPreview.map(t => t.accountId).filter(Boolean))];
                        return uniqueAccounts.length > 0 ? `${uniqueAccounts.length} detected` : 'None in CSV';
                      })()}
                    </span>
                  </div>
                  <div>
                    <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>Total P&L: </span>
                    <span className={(() => {
                      const total = importPreview.reduce((sum, t) => sum + (parseFloat(t.ntProfit) || 0), 0);
                      return total >= 0 ? 'text-green-500' : 'text-red-500';
                    })()}>
                      ${importPreview.reduce((sum, t) => sum + (parseFloat(t.ntProfit) || 0), 0).toFixed(2)}
                    </span>
                  </div>
                  {(() => {
                    const totalComm = importPreview.reduce((sum, t) => sum + (t.importedCommission || 0), 0);
                    if (totalComm > 0) {
                      return (
                        <div>
                          <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>Total Comm: </span>
                          <span className="text-orange-500">${totalComm.toFixed(2)}</span>
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>
              </div>

              {/* Unmatched positions warning */}
              {unmatchedWarning && unmatchedWarning.length > 0 && (
                <div className={`mt-3 p-3 rounded-lg border ${darkMode ? 'bg-yellow-900/30 border-yellow-700' : 'bg-yellow-50 border-yellow-300'}`}>
                  <p className={`text-xs font-medium ${darkMode ? 'text-yellow-400' : 'text-yellow-700'}`}>
                    {unmatchedWarning.length} unmatched position{unmatchedWarning.length !== 1 ? 's' : ''} (not imported):
                  </p>
                  <ul className={`mt-1 text-xs ${darkMode ? 'text-yellow-300/80' : 'text-yellow-600'}`}>
                    {unmatchedWarning.map((u, i) => (
                      <li key={i}>{u.side.toUpperCase()} {u.quantity} {u.symbol} @ {u.price} ({u.date})</li>
                    ))}
                  </ul>
                  <p className={`mt-1 text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    These fills had no matching opposite fill to close the position.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>


        {/* Grouping Options — trades-mode only; hidden for sources that do FIFO matching internally */}
        {importType === 'trades' && importPreview.length > 0 && importPreview[0].entryTime && importSource !== 'motivewave' && importSource !== 'tradovate-fills' && (
          <div className={`mb-4 p-4 rounded-lg border ${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-yellow-50 border-yellow-200'}`}>
            <div className="flex items-center justify-between mb-2">
              <label className={`text-sm font-medium ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                🔗 Group Partial Fills
              </label>
              <button
                onClick={() => setGroupByEntryTime(!groupByEntryTime)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  groupByEntryTime ? 'bg-green-500' : darkMode ? 'bg-gray-600' : 'bg-gray-300'
                }`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  groupByEntryTime ? 'translate-x-6' : 'translate-x-1'
                }`} />
              </button>
            </div>
            <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              {groupByEntryTime 
                ? 'Combining partial fills with same entry time into single trades'
                : 'Each row will be imported as a separate trade'}
            </p>
          </div>
        )}

        {/* Account Selector — trades mode only (chart-data has no account concept) */}
        {importType === 'trades' && (
        <div className="mb-4">
          <label className={`block text-sm font-medium mb-2 transition-colors duration-300 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
            Assign to Account (for trades without account):
          </label>
          <GlassSelect
            value={importAccountId}
            onChange={(v) => setImportAccountId(v)}
            options={[
              { value: '', label: 'No account (use account from CSV if available)' },
              ...accounts.map(account => ({ value: account.id, label: account.name })),
            ]}
            darkMode={darkMode}
            className="w-full"
          />
        </div>
        )}

        {/* New Accounts from Import — trades mode only */}
        {importType === 'trades' && newAccountsFromImport.length > 0 && (
          <div className={`mb-4 p-4 rounded-lg border ${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-blue-50 border-blue-200'}`}>
            <h4 className={`text-sm font-medium mb-3 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
              📋 New accounts detected in CSV:
            </h4>
            <div className="space-y-2">
              {newAccountsFromImport.map((account) => (
                <div key={account.id} className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={account.selected}
                    onChange={(e) => {
                      setNewAccountsFromImport(newAccountsFromImport.map(a => 
                        a.id === account.id ? { ...a, selected: e.target.checked } : a
                      ));
                    }}
                    className="w-4 h-4 text-blue-500 rounded-sm"
                  />
                  <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    {account.name}
                  </span>
                  <span className={`text-xs px-2 py-0.5 rounded ${
                    darkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-600'
                  }`}>
                    {account.broker}
                  </span>
                  <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    ${account.commissionPerSide}/side
                  </span>
                </div>
              ))}
            </div>
            <p className={`text-xs mt-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Selected accounts will be created automatically
            </p>
          </div>
        )}

        {/* Footer Buttons — mode-aware. Trades mode keeps the legacy
            Cancel + Import N Trades pair (with dedup/enrich callouts).
            Chart-data mode swaps Import for a Save button that's enabled
            only when a valid file is parsed and the platform is supported. */}
        <div className="flex justify-end gap-3 items-center">
          {importType === 'trades' && (duplicateCount > 0 || enrichCount > 0) && (
            <div className="flex flex-col items-end gap-0.5">
              {enrichCount > 0 && (
                <span className={`text-sm ${darkMode ? 'text-cyan-400' : 'text-cyan-600'}`}>
                  {enrichCount} existing trade{enrichCount !== 1 ? 's' : ''} will be enriched with MAE/MFE
                </span>
              )}
              {duplicateCount > 0 && (
                <span className={`text-sm ${darkMode ? 'text-yellow-400' : 'text-yellow-600'}`}>
                  {duplicateCount} duplicate{duplicateCount !== 1 ? 's' : ''} will be skipped
                </span>
              )}
            </div>
          )}
          <button
            onClick={closeModal}
            className={`px-4 py-2 rounded-lg border transition-[background-color,border-color,box-shadow,color] duration-150 ${
              darkMode
                ? 'bg-white/[0.04] border-white/10 text-gray-300 hover:bg-white/[0.08] hover:border-white/20 hover:text-white'
                : 'bg-white/60 border-gray-200 text-gray-700 hover:bg-white/80 hover:border-gray-300 hover:text-gray-900'
            }`}
          >
            {chartSaveState === 'saved' ? 'Done' : 'Cancel'}
          </button>
          {importType === 'trades' ? (
            <button
              onClick={confirmImport}
              disabled={importPreview.length === 0 || importPreview.length - duplicateCount === 0}
              className={`px-4 py-2 rounded-lg border transition-[background-color,border-color,box-shadow,color] duration-150 disabled:opacity-40 disabled:cursor-not-allowed ${
                darkMode
                  ? 'bg-blue-500/25 border-blue-400/50 text-blue-50 ring-1 ring-inset ring-blue-400/25 hover:bg-blue-500/35 hover:border-blue-400/70 hover:shadow-[0_0_18px_rgba(59,130,246,0.3)]'
                  : 'bg-blue-100 border-blue-300 text-blue-800 hover:bg-blue-200 hover:border-blue-400'
              }`}
            >
              Import {importPreview.length - duplicateCount} Trade{importPreview.length - duplicateCount !== 1 ? 's' : ''}
            </button>
          ) : (
            <button
              onClick={() => handleChartSave(false)}
              disabled={
                !isChartDataSupported(importSource)
                || !chartFileMeta
                || !!chartFileMeta.error
                || chartFileMeta.bars.length === 0
                || chartSaveState === 'saving'
                || chartSaveState === 'saved'
              }
              className={`px-4 py-2 rounded-lg border transition-[background-color,border-color,box-shadow,color] duration-150 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5 ${
                darkMode
                  ? 'bg-purple-500/25 border-purple-400/50 text-purple-50 ring-1 ring-inset ring-purple-400/25 hover:bg-purple-500/35 hover:border-purple-400/70 hover:shadow-[0_0_18px_rgba(168,85,247,0.3)]'
                  : 'bg-purple-100 border-purple-300 text-purple-800 hover:bg-purple-200 hover:border-purple-400'
              }`}
            >
              <Sparkles size={13} />
              {chartSaveState === 'saving' ? 'Saving…'
                : chartSaveState === 'saved' ? 'Saved'
                : chartFileMeta && !chartFileMeta.error
                  ? `Save ${chartFileMeta.parsed.toLocaleString()} bars to Library`
                  : 'Save to Library'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImportModal;
